import React, { useState } from 'react';
import BlogCategory from '../../../components/Blogs/BlogCategory/BlogCategory';
import { Author, BlogCity } from '../../blog';
import axios from 'axios';
import { useRouter } from 'next/router';
import { BlogCard } from '../../../types/pages/author';
import Head from 'next/head';
import Image from 'next/image';
import { replaceTripshepherdWithSeeSight } from '../../../utils/trip-sheppered-text';

interface BlogCategoryPageProps {
  authors: Author[];
  blogs: BlogCard[];
  cities: BlogCity[];
  blogCategoryPageData: BlogCategoryData;
  totalBlogPages: number;
  currentBlogPage: number;
}

interface Params {
  params: {
    categoryId: string;
  };
}

export default function BlogCategoryPage({
  authors,
  blogs,
  cities,
  blogCategoryPageData,
  totalBlogPages,
  currentBlogPage
}: BlogCategoryPageProps) {
  const [paginatedBlogs, setPaginatedBlogs] = useState<BlogCard[]>(blogs);
  const [currentPage, setCurrentPage] = useState(currentBlogPage);
  const [totalPages, setTotalPages] = useState(totalBlogPages);
  const [cityId, setCityId] = useState<string | null>('all-blogs');
  const router = useRouter();

  const handlePaginationClick = async (page: number) => {
    setCurrentPage(page);
    await fetchBlogs(page, cityId);
  };

  const handleCityFilterChange = async (cityId: string) => {
    setCityId(cityId);
    setCurrentPage(1);
    await fetchBlogs(1, cityId);
  };

  // for handling pagination and city filters on client side
  const fetchBlogs = async (page: number, cityId: string | null) => {
    try {
      const res = await axios.get(
        `${process.env.NEXT_PUBLIC_FIREBASE_CONNECTION}/blogs`,
        {
          params: {
            order: 'desc',
            page,
            category: router.query.categoryId,
            limit: 8,
            ...(cityId && cityId !== 'all-blogs' && { city: cityId })
          }
        }
      );
      setPaginatedBlogs(
        res.data.blogs.map((blog: any) => {
          return {
            name: blog.page_title,
            publication_date: blog.publication_date,
            snippet: blog.snippet,
            image: {
              alternativeText: blog.page_title,
              url: blog.image
            },
            slug: blog.slug,
            author: blog.author
          };
        })
      );

      setTotalPages(res.data.totalPages);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  return (
    <>
      <Head>
        <meta
          name="og:title"
          content={replaceTripshepherdWithSeeSight(
            blogCategoryPageData?.meta_title
          )}
        />
        <meta
          name="og:description"
          content={replaceTripshepherdWithSeeSight(
            blogCategoryPageData?.meta_description
          )}
          key="metadescription"
        />
        <title>
          {replaceTripshepherdWithSeeSight(blogCategoryPageData?.meta_title)}
        </title>
        <meta name="og:image" content="/vertical-seesight-logo.png" />
        <meta
          name="description"
          content={replaceTripshepherdWithSeeSight(
            blogCategoryPageData?.meta_description
          )}
          key="metadescription"
        />
        <link
          href={replaceTripshepherdWithSeeSight(
            blogCategoryPageData?.canonical
          )}
          rel="canonical"
          key="canonical"
        />
      </Head>

      {blogCategoryPageData ? (
        <BlogCategory
          blogCategoryData={blogCategoryPageData}
          authors={authors}
          blogs={replaceTripshepherdWithSeeSight(paginatedBlogs)}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePaginationClick}
          onCityChange={handleCityFilterChange}
          cities={cities}
        />
      ) : (
        <div className="flex h-[calc(100vh-100px)] justify-center items-center gap-5 flex-col px-10 xl:mr-32">
          <Image
            src="/missing-data-illustration.svg"
            alt="missing data icon"
            width={272}
            height={181}
            className="w-[200px] h-[150px] sm:w-[272px] sm:h-[181px]"
          />
          <h2 className="text-black13 text-xs sm:text-base lg:text-lg font-semibold leading-none">
            No Blogs Category Data Found!
          </h2>
          <div className="text-center text-gray75 text-xs sm:text-base font-normal max-w-[400px]">
            We couldn&apos;t find information about the blogs categories. Please
            check back later or explore other content.
          </div>
          <button
            className="h-[32] bg-primary text-white text-center text-xs sm:text-sm px-5 rounded-[30px] py-2 sm:py-3 shadow"
            onClick={() => router.push('/')}
          >
            Explore Tours
          </button>
        </div>
      )}
    </>
  );
}

export async function getStaticPaths() {
  try {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_FIREBASE_CONNECTION}/blog-page`
    );
    const data = await res.json();

    if (!data || !data.blog_categories) {
      throw new Error('No categories found in the response');
    }

    const paths = data?.blog_categories
      ?.filter((category: any) => category.id)
      ?.map((category: any) => ({
        params: { categoryId: category.id.toString() }
      }));

    return {
      paths,
      fallback: 'blocking'
    };
  } catch (error) {
    console.error('Error in getStaticPaths:', error);
    return {
      paths: [],
      fallback: 'blocking'
    };
  }
}

export const getStaticProps = async ({ params }: Params) => {
  const { categoryId } = params;

  if (!categoryId) {
    console.error('categoryId is missing in getStaticProps');
    return {
      notFound: true
    };
  }

  try {
    const [blogsRes, blogsDataRes, blogCategoryPageRes] = await Promise.all([
      axios.get(
        `${process.env.NEXT_PUBLIC_FIREBASE_CONNECTION}/blogs?order=desc&page=1&category=${categoryId}&limit=8`
      ),
      axios.get(`${process.env.NEXT_PUBLIC_FIREBASE_CONNECTION}/blog-page`),
      axios.get(
        `${process.env.NEXT_PUBLIC_FIREBASE_CONNECTION}/blog-category/${categoryId}`
      )
    ]);

    const blogs: BlogCard[] = blogsRes?.data?.blogs.map((blog: any) => {
      return {
        name: blog.page_title,
        publication_date: blog.publication_date,
        snippet: blog.snippet,
        image: {
          alternativeText: blog.page_title || 'No Image Available',
          // Provide a fallback URL or use null if the image URL is undefined
          url: blog.image ? blog.image || '' : ''
        },
        slug: blog.slug,
        author: blog.author
      };
    });

    const { totalPages, currentPage } = blogsRes?.data;

    const authors: Author[] = blogsDataRes?.data.authors;

    const updatedCities = [
      { name: 'All Blogs', id: 'all-blogs' },
      ...blogsDataRes.data.cities
    ];

    const blogCategoryPageData = blogCategoryPageRes?.data;

    return {
      props: {
        blogs:
          blogs &&
          replaceTripshepherdWithSeeSight(JSON.parse(JSON.stringify(blogs))),
        authors,
        cities: updatedCities,
        blogCategoryPageData:
          blogCategoryPageData &&
          replaceTripshepherdWithSeeSight(
            JSON.parse(JSON.stringify(blogCategoryPageData))
          ),
        totalBlogPages: totalPages,
        currentBlogPage: currentPage
      }
    };
  } catch (error) {
    console.error('Error in getStaticProps:', error);
    return {
      props: {
        blogs: [],
        authors: [],
        cities: [],
        blogCategoryPageData: null
      }
    };
  }
};

export interface BlogCategoryData {
  image: string;
  name: string;
  meta_description: string;
  meta_title: string;
  canonical: string;
}
